import { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMarketData } from '../features/markets/marketsSlice';

// Default staleness threshold: 1 hour (in milliseconds)
const DEFAULT_STALE_THRESHOLD = 60 * 60 * 1000;

// Check interval: 5 minutes
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Custom hook to automatically refresh stale chart data
 * 
 * Refreshes market data when:
 * - Data is older than the threshold (default: 1 hour)
 * - Tab becomes visible after being backgrounded
 * - Periodic checks every 5 minutes
 * - On initial mount if data is already stale
 * 
 * @param {string} symbol - The trading symbol (e.g., 'BTC/USD' or 'BTC-USD')
 * @param {string} timeframe - The timeframe (e.g., 'daily', 'hourly', 'weekly')
 * @param {number} thresholdMs - Stale threshold in ms (default: 1 hour)
 * @returns {Object} - { dataAgeMs, isStale, isRefreshing, lastUpdatedAt, refresh }
 */
const useStaleDataRefresh = (symbol, timeframe = 'daily', thresholdMs = DEFAULT_STALE_THRESHOLD) => {
  const dispatch = useDispatch();
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);
  
  // Local refresh state - isolated from global market operations
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Ref to track refreshing state for callbacks (avoids dependency cascade)
  const isRefreshingRef = useRef(false);
  
  // Get market data entry from Redux
  const marketDataEntry = useSelector((state) => {
    if (!symbol || !state.markets?.marketData) return null;
    // Handle both symbol formats (BTC/USD and BTC-USD)
    const normalizedSymbol = symbol.replace('/', '-');
    return state.markets.marketData[normalizedSymbol] || state.markets.marketData[symbol];
  });

  const lastUpdatedAt = marketDataEntry?.lastUpdatedAt || null;
  
  // Calculate data age
  const dataAgeMs = lastUpdatedAt ? Date.now() - lastUpdatedAt : null;
  const isStale = dataAgeMs !== null && dataAgeMs > thresholdMs;

  // Keep ref in sync with refreshing state (avoids stale closures in callbacks)
  useEffect(() => {
    isRefreshingRef.current = isRefreshing;
  }, [isRefreshing]);

  // Function to refresh data
  const refresh = useCallback(async () => {
    // Use ref to check refreshing state - avoids dependency cascade
    if (!symbol || !mountedRef.current || isRefreshingRef.current) return;
    
    setIsRefreshing(true);
    try {
      const normalizedSymbol = symbol.replace('/', '-');
      await dispatch(fetchMarketData({ 
        symbol: normalizedSymbol, 
        timeframe,
        forceFullFetch: false // Use incremental fetch for efficiency
      })).unwrap();
    } catch (error) {
      console.warn('useStaleDataRefresh: Failed to refresh chart data:', error);
    } finally {
      if (mountedRef.current) {
        setIsRefreshing(false);
      }
    }
  }, [dispatch, symbol, timeframe]);

  // Check if data needs refresh
  const checkAndRefresh = useCallback(() => {
    // Use ref to avoid interval reset on every refresh state change
    if (!mountedRef.current || isRefreshingRef.current) return;
    
    // Recalculate staleness at check time
    const currentAge = lastUpdatedAt ? Date.now() - lastUpdatedAt : null;
    const currentlyStale = currentAge !== null && currentAge > thresholdMs;
    
    if (currentlyStale) {
      refresh();
    }
  }, [lastUpdatedAt, thresholdMs, refresh]);

  // Handle visibility change - refresh when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Small delay to avoid rapid-fire refreshes
        setTimeout(() => {
          if (mountedRef.current) {
            checkAndRefresh();
          }
        }, 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkAndRefresh]);

  // Set up interval to check for stale data
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (!symbol) return;

    // Set up new interval - only depends on symbol, not on refresh state
    intervalRef.current = setInterval(() => {
      checkAndRefresh();
    }, CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [symbol, checkAndRefresh]);

  // Check on mount
  useEffect(() => {
    mountedRef.current = true;
    
    // Initial staleness check with small delay
    const timer = setTimeout(() => {
      if (mountedRef.current && symbol) {
        checkAndRefresh();
      }
    }, 500);
    
    return () => {
      mountedRef.current = false;
      clearTimeout(timer);
    };
  }, [symbol, checkAndRefresh]); // checkAndRefresh is stable now

  return {
    dataAgeMs,
    isStale,
    isRefreshing,
    lastUpdatedAt,
    refresh,
  };
};

export default useStaleDataRefresh;
